import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useAppStore } from '@core/store'
import { useNomesDosModelos } from '@viewer/lib/useNomeDoModelo'
import type { ClashResult } from '@viewer/clash/lib'
import {
  idDeRegraNova,
  patchDeReabrir,
  patchDeVoltarARegra,
  regrasQueApanham,
  textoDaCondicao,
  type AvaliacaoDasExclusoes,
  type CondicaoDeExclusao,
  type EspecialidadeDe,
  type RegraDeExclusao,
} from './clashExclusao'
import { clashKey, clashLabel, textoParaLista } from './clashTriage'

// ---------------------------------------------------------------------------
// Painel "Regras de exclusão": o segundo passo do funil (clashExclusao.ts).
//
// Cada regra com o que apanharia agora (ligada ou nao), a condicao por
// extenso e o motivo. Ligar uma regra esconde os conflitos dela da lista
// de trabalho e da exportacao; a lista dos ignorados abre-se aqui para os
// rever e reabrir um a um, ou a regra inteira (desligando-a). As regras
// de duplicacao/modelacao tem "Criar topico para o modelador": um topico
// BCF com os elementos, em vez de um por conflito.
// ---------------------------------------------------------------------------

const CAMPO =
  'rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-[11px]'
const BOTAO =
  'rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[11px] disabled:opacity-40'

/** Conflitos listados por regra, no maximo. */
const MAXIMO_NA_LISTA = 200

export interface ClashExclusaoPainelProps {
  results: readonly ClashResult[]
  avaliacao: AvaliacaoDasExclusoes
  especialidadeDe: EspecialidadeDe
  aberto: boolean
  onAberto: (on: boolean) => void
  /** "Criar tópico para o modelador" (quem chama sabe o destino). */
  onTopicoModelador: (regra: RegraDeExclusao, results: ClashResult[]) => Promise<void>
}

interface Rascunho {
  nome: string
  motivo: string
  regras: string
  tipos: string
  kind: '' | 'hard' | 'clearance'
  especialidadesA: string
  classesA: string
  especialidadesB: string
  classesB: string
  mesmaEspecialidade: boolean
  profundidadeMaxMm: string
  folgaMaxMm: string
  niveis: string
  naOrigem: boolean
}

const RASCUNHO_VAZIO: Rascunho = {
  nome: '',
  motivo: '',
  regras: '',
  tipos: '',
  kind: '',
  especialidadesA: '',
  classesA: '',
  especialidadesB: '',
  classesB: '',
  mesmaEspecialidade: false,
  profundidadeMaxMm: '',
  folgaMaxMm: '',
  niveis: '',
  naOrigem: false,
}

function condicaoDoRascunho(r: Rascunho): CondicaoDeExclusao {
  const c: CondicaoDeExclusao = {}
  const lista = (t: string, maiusculas = true) => {
    const l = textoParaLista(t).map((x) => (maiusculas ? x.toUpperCase() : x))
    return l.length > 0 ? l : undefined
  }
  const num = (t: string) => {
    const n = Number(t.replace(',', '.'))
    return t.trim() !== '' && Number.isFinite(n) && n >= 0 ? n : undefined
  }
  const regras = lista(r.regras)
  if (regras) c.regras = regras
  const tipos = lista(r.tipos, false)
  if (tipos) c.tipos = tipos
  if (r.kind) c.kind = r.kind
  const ea = lista(r.especialidadesA)
  if (ea) c.especialidadesA = ea
  const ca = lista(r.classesA)
  if (ca) c.classesA = ca
  const eb = lista(r.especialidadesB)
  if (eb) c.especialidadesB = eb
  const cb = lista(r.classesB)
  if (cb) c.classesB = cb
  if (r.mesmaEspecialidade) c.mesmaEspecialidade = true
  const p = num(r.profundidadeMaxMm)
  if (p !== undefined) c.profundidadeMaxMm = p
  const f = num(r.folgaMaxMm)
  if (f !== undefined) c.folgaMaxMm = f
  const n = lista(r.niveis, false)
  if (n) c.niveis = n
  return c
}

export default function ClashExclusaoPainel({
  results,
  avaliacao,
  especialidadeDe,
  aberto,
  onAberto,
  onTopicoModelador,
}: ClashExclusaoPainelProps) {
  const exclusoes = useAppStore((s) => s.clashClassificacao.exclusoes)
  const setClassificacao = useAppStore((s) => s.setClashClassificacao)
  const triagem = useAppStore((s) => s.clashTriage)
  const applyTriage = useAppStore((s) => s.applyClashTriage)
  const pushToast = useAppStore((s) => s.pushToast)
  const { nomeDe } = useNomesDosModelos()

  const [listaAberta, setListaAberta] = useState<string | null>(null)
  const [novaAberta, setNovaAberta] = useState(false)
  const [rascunho, setRascunho] = useState<Rascunho>(RASCUNHO_VAZIO)
  const [ocupado, setOcupado] = useState<string | null>(null)

  const porChave = useMemo(() => new Map(results.map((r) => [clashKey(r), r])), [results])
  const nIgnorados = avaliacao.ignorados.size

  const gravar = (regras: RegraDeExclusao[]) => setClassificacao({ exclusoes: regras })
  const ligar = (id: string, ativa: boolean) => gravar(exclusoes.map((r) => (r.id === id ? { ...r, ativa } : r)))
  const apagar = (id: string) => gravar(exclusoes.filter((r) => r.id !== id))

  const rotulo = (k: string) => {
    const r = porChave.get(k)
    if (!r) return k
    const ma = nomeDe(r.pair.modelA)?.minimo ?? r.pair.modelA
    const mb = nomeDe(r.pair.modelB)?.minimo ?? r.pair.modelB
    return `${ma} ${r.categoryA ?? 'elemento'} #${r.pair.localIdA} x ${mb} ${r.categoryB ?? 'elemento'} #${r.pair.localIdB}`
  }

  const reabrir = (k: string) => {
    const r = porChave.get(k)
    const ids = regrasQueApanham(k, r, triagem[k], exclusoes, especialidadeDe)
    if (ids.length === 0) return
    applyTriage(k, { ...patchDeReabrir(triagem[k], ids), ...(r ? { label: clashLabel(r) } : {}) })
  }

  const devolver = (k: string, id: string) => {
    applyTriage(k, patchDeVoltarARegra(triagem[k], id))
  }

  const acrescentar = () => {
    const condicao = condicaoDoRascunho(rascunho)
    const nome = rascunho.nome.trim()
    if (!nome) return
    const regra: RegraDeExclusao = {
      id: idDeRegraNova(nome, exclusoes),
      nome: nome.slice(0, 120),
      motivo: rascunho.motivo.trim().slice(0, 1000),
      ativa: false,
      ...(rascunho.naOrigem ? { naOrigem: true } : {}),
      condicao,
    }
    gravar([...exclusoes, regra])
    setRascunho(RASCUNHO_VAZIO)
    setNovaAberta(false)
    pushToast(`Regra "${regra.nome}" acrescentada, desligada. Ligue-a para esconder os conflitos que apanha.`, {
      type: 'success',
    })
  }

  const topico = async (regra: RegraDeExclusao) => {
    const chaves = avaliacao.porRegra[regra.id]?.apanha ?? []
    const rs = chaves.map((k) => porChave.get(k)).filter((r): r is ClashResult => !!r)
    if (rs.length === 0) return
    setOcupado(regra.id)
    try {
      await onTopicoModelador(regra, rs)
    } finally {
      setOcupado(null)
    }
  }

  const campo = (k: keyof Rascunho, rotuloDoCampo: string, dica?: string) => (
    <label className="flex flex-col gap-0.5" title={dica}>
      <span className="text-[var(--color-text-dim)]">{rotuloDoCampo}</span>
      <input
        value={rascunho[k] as string}
        onChange={(e) => setRascunho({ ...rascunho, [k]: e.target.value })}
        className={CAMPO}
        aria-label={rotuloDoCampo}
      />
    </label>
  )

  return (
    <div className="border-b border-[var(--color-border)] text-[11px]" data-testid="clash-exclusao">
      <button
        type="button"
        onClick={() => onAberto(!aberto)}
        className="flex w-full items-center gap-1 px-3 py-1.5 text-left text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        aria-expanded={aberto}
      >
        {aberto ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        Regras de exclusão
        <span className="ml-auto text-[10px] text-[var(--color-text-dim)]">
          {exclusoes.filter((r) => r.ativa).length} ligada{exclusoes.filter((r) => r.ativa).length === 1 ? '' : 's'} ·
          Ignorados por regra ({nIgnorados})
        </span>
      </button>
      {aberto && (
        <div className="flex flex-col gap-1.5 px-3 pb-2">
          <p className="leading-snug text-[var(--color-text-dim)]">
            Os conflitos apanhados por uma regra ligada saem da lista de trabalho e da exportação BCF, e
            ficam aqui para rever e reabrir. Só se apanham conflitos por decidir: um aceite ou um
            resolvido fica na lista com o seu estado. A contagem diz o que cada regra apanha agora, ligada
            ou não.
          </p>
          {exclusoes.map((regra) => {
            const x = avaliacao.porRegra[regra.id] ?? { apanha: [], reabertos: [] }
            const aberta = listaAberta === regra.id
            return (
              <div key={regra.id} className="rounded border border-[var(--color-border)]/50 px-1.5 py-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={regra.ativa}
                      onChange={(e) => ligar(regra.id, e.target.checked)}
                      className="accent-[var(--color-accent)]"
                      aria-label={`Ligar a regra ${regra.nome}`}
                    />
                    <span className="text-[var(--color-text)]">{regra.nome}</span>
                  </label>
                  {regra.sugerida && !regra.ativa && (
                    <span className="text-[10px] text-[var(--color-text-dim)]">sugerida</span>
                  )}
                  <span className="ml-auto font-mono text-[10px] text-[var(--color-text-muted)]">
                    {regra.ativa ? 'ignora' : 'apanharia'} {x.apanha.length}
                    {x.reabertos.length > 0 ? ` · ${x.reabertos.length} reaberto${x.reabertos.length === 1 ? '' : 's'}` : ''}
                  </span>
                </div>
                <p className="text-[10px] leading-snug text-[var(--color-text-dim)]">
                  Condição: {textoDaCondicao(regra.condicao)}.
                  {regra.motivo ? ` Motivo: ${regra.motivo}` : ''}
                </p>
                <div className="mt-0.5 flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => setListaAberta(aberta ? null : regra.id)}
                    disabled={x.apanha.length + x.reabertos.length === 0}
                    className={BOTAO}
                    aria-expanded={aberta}
                  >
                    {aberta ? 'Fechar a lista' : `Ver os conflitos (${x.apanha.length + x.reabertos.length})`}
                  </button>
                  {regra.ativa && x.apanha.length > 0 && (
                    <button
                      type="button"
                      onClick={() => ligar(regra.id, false)}
                      className={BOTAO}
                      title="Desliga a regra: os conflitos dela voltam todos para a lista de trabalho"
                    >
                      Reabrir a regra inteira
                    </button>
                  )}
                  {regra.naOrigem && (
                    <button
                      type="button"
                      onClick={() => void topico(regra)}
                      disabled={x.apanha.length === 0 || ocupado !== null}
                      className={BOTAO}
                      title="Um só tópico BCF para o modelador, com a regra e a lista dos elementos (GlobalId), em vez de um tópico por conflito"
                    >
                      {ocupado === regra.id ? 'A criar…' : 'Criar tópico para o modelador'}
                    </button>
                  )}
                  {!regra.sugerida && (
                    <button
                      type="button"
                      onClick={() => apagar(regra.id)}
                      className={BOTAO}
                      title="Apaga a regra; os conflitos dela voltam para a lista"
                    >
                      Apagar a regra
                    </button>
                  )}
                </div>
                {aberta && (
                  <ul className="mt-1 flex max-h-48 flex-col gap-0.5 overflow-auto text-[10px]">
                    {x.apanha.slice(0, MAXIMO_NA_LISTA).map((k) => (
                      <li key={k} className="flex items-center gap-1">
                        <span className="min-w-0 flex-1 truncate text-[var(--color-text-muted)]" title={rotulo(k)}>
                          {triagem[k]?.regra ? `${triagem[k].regra} · ` : ''}
                          {rotulo(k)}
                        </span>
                        {regra.ativa && (
                          <button type="button" onClick={() => reabrir(k)} className={BOTAO}>
                            Reabrir
                          </button>
                        )}
                      </li>
                    ))}
                    {x.apanha.length > MAXIMO_NA_LISTA && (
                      <li className="text-[var(--color-text-dim)]">
                        E mais {x.apanha.length - MAXIMO_NA_LISTA}.
                      </li>
                    )}
                    {x.reabertos.slice(0, MAXIMO_NA_LISTA).map((k) => (
                      <li key={k} className="flex items-center gap-1">
                        <span className="min-w-0 flex-1 truncate text-[var(--color-text)]" title={rotulo(k)}>
                          Reaberto: {rotulo(k)}
                        </span>
                        <button
                          type="button"
                          onClick={() => devolver(k, regra.id)}
                          className={BOTAO}
                          title="Volta a ser apanhado por esta regra"
                        >
                          Devolver à regra
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}

          <button
            type="button"
            onClick={() => setNovaAberta(!novaAberta)}
            className="flex items-center gap-1 text-left text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            aria-expanded={novaAberta}
          >
            {novaAberta ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Nova regra
          </button>
          {novaAberta && (
            <div className="flex flex-col gap-1 rounded border border-[var(--color-border)]/50 px-1.5 py-1">
              <div className="grid grid-cols-1 gap-1 @md:grid-cols-2">
                {campo('nome', 'Nome')}
                {campo('motivo', 'Motivo')}
                {campo('regras', 'Códigos da classificação', 'Separados por vírgulas: B1, D1, E1, ...')}
                {campo('tipos', 'Tipo de problema', 'atravessamento, embebido, duplicação, ...')}
                {campo('especialidadesA', 'Especialidades de um lado', 'ARQ, EST, MAC, HID, EEL; MEP vale as três redes')}
                {campo('classesA', 'Classes IFC desse lado', 'IFCFURNITURE, IFCDOOR, ...')}
                {campo('especialidadesB', 'Especialidades do outro lado')}
                {campo('classesB', 'Classes IFC do outro lado')}
                {campo('profundidadeMaxMm', 'Profundidade máxima (mm)')}
                {campo('folgaMaxMm', 'Folga máxima (mm)')}
                {campo('niveis', 'Níveis', 'P1, P2, ...')}
                <label className="flex flex-col gap-0.5">
                  <span className="text-[var(--color-text-dim)]">Interferência ou folga</span>
                  <select
                    value={rascunho.kind}
                    onChange={(e) => setRascunho({ ...rascunho, kind: e.target.value as Rascunho['kind'] })}
                    className={CAMPO}
                    aria-label="Interferência ou folga"
                  >
                    <option value="">as duas</option>
                    <option value="hard">interferências duras</option>
                    <option value="clearance">folgas</option>
                  </select>
                </label>
              </div>
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={rascunho.mesmaEspecialidade}
                  onChange={(e) => setRascunho({ ...rascunho, mesmaEspecialidade: e.target.checked })}
                  className="accent-[var(--color-accent)]"
                />
                Os dois elementos da mesma especialidade
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={rascunho.naOrigem}
                  onChange={(e) => setRascunho({ ...rascunho, naOrigem: e.target.checked })}
                  className="accent-[var(--color-accent)]"
                />
                Corrige-se na origem (um tópico para o modelador)
              </label>
              <p className="text-[10px] text-[var(--color-text-dim)]">
                Condição: {textoDaCondicao(condicaoDoRascunho(rascunho))}.
              </p>
              <div>
                <button type="button" onClick={acrescentar} disabled={!rascunho.nome.trim()} className={BOTAO}>
                  Acrescentar a regra (desligada)
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
